import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { AdminFormRow } from 'shared-components';
import Button, { BUTTON_TYPES } from 'shared-components/Button/Button';
import {
  showErrorToast,
  showSuccessToast,
} from 'shared-components/BaseToast/Toast';
import useFormData from 'hooks/useFormData';
import { createProduct, updateProduct } from 'services/api/admin-products';
import { deleteImagesFromCdn } from 'services/api/admin-images';
import {
  validateForm,
  formatProductToDisplay,
  formatSkuText,
  formatAllData,
  getPublicSwitchEnabled,
  validateRequiredPublicFields,
  getImagesToDelete,
} from 'utils/admin-products';
import ProductMetadataForm from '../ProductMetadataForm/ProductMetadataForm';
import ProductInformationForm from '../ProductInformationForm/ProductInformationForm';
import Tab from './Tab';
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch';
import styles from './ProductForm.module.css';

const ProductForm = ({
  title,
  initialProductData,
  initialMetadataMap,
  setInitialProductData,
  setInitialMetadataMap,
}) => {
  const history = useHistory();
  const [tab, setTab] = useState('productInformation');
  const [loading, setLoading] = useState(false);
  const [
    requiredPublicProductFields,
    setRequiredPublicProductFields,
  ] = useState({});
  const [
    requiredPublicVariantFields,
    setRequiredPublicVariantFields,
  ] = useState([]);
  const [variantErrors, setVariantErrors] = useState([]);
  const [metadataFormData, setMetadataFormData] = useState(null);

  const {
    setFormData,
    handleInputChange,
    handleCheckbox,
    handleErrors,
    setFormErrors,
    formData: productFormData,
    formErrors: productFormErrors,
    resetField,
  } = useFormData();
  const { isPublic } = productFormData;
  const isSaved = _.has(productFormData, 'productId');
  const { register, formState, watch, reset, setValue } = useForm({});
  const {
    dirtyFields: metadataDirtyFields,
    isDirty: metadataFormDirty,
  } = formState;

  const isUpdated =
    !_.isEqual(initialProductData, productFormData) || metadataFormDirty;

  useEffect(() => {
    //if initialProductData is passed as a prop from the EditProduct component, we want to use that as the initial form data
    if (initialProductData) {
      setFormData(initialProductData);
    } else {
      //otherwise we are passing an initial form data object with one variant in it
      setFormData({ productCatalogVariants: [{}] });
    }
  }, [initialProductData]);

  const productMetadata = watch();

  const handleClickTab = (tab) => {
    if (metadataFormDirty && tab === 'productInformation') {
      setMetadataFormData(productMetadata);
    }
    setTab(tab);
  };

  // delete any images that are in the initial data but not form data
  const deleteRemovedImages = () => {
    const imagesToDelete = getImagesToDelete(
      initialProductData,
      productFormData
    );
    if (imagesToDelete.length) {
      deleteImagesFromCdn(imagesToDelete);
    }
  };

  // delete any images that are in form data but not initial data
  const deleteUnsavedImages = () => {
    const imagesToDelete = getImagesToDelete(
      productFormData,
      initialProductData
    );
    if (imagesToDelete.length) {
      deleteImagesFromCdn(imagesToDelete);
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    const { productCatalogVariants } = productFormData;
    const isValid = validateForm(
      handleErrors,
      productFormData,
      setVariantErrors
    );
    if (!isValid) {
      showErrorToast(
        'Form Validation Failed',
        'There are some input fields that need to be addressed before you can successfully save this product. Please update and try again.'
      );
      return setLoading(false);
    }
    const submitData = formatAllData(
      initialProductData,
      productFormData,
      productMetadata,
      metadataDirtyFields
    );
    if (!_.isEmpty(submitData)) {
      const action = isSaved ? updateProduct : createProduct;
      try {
        const response = await action(submitData);
        deleteRemovedImages();
        const { value } = response;
        const formattedProduct = formatProductToDisplay(value);
        //we don't need to do this for the create form since we are redirecting to the edit page
        if (isSaved) {
          const { variantMetadataMap } = value;
          setFormData(formattedProduct);
          setInitialProductData(formattedProduct);
          setInitialMetadataMap(variantMetadataMap);
        }
        const toastTitle = isSaved
          ? 'Product Successfully Updated'
          : 'Product Successfully Created';
        const toastSubtitle = formatSkuText(value.productCatalogVariants);
        showSuccessToast(toastTitle, toastSubtitle);
        setLoading(false);
        history.push(`/admin/product-catalog/edit/${value.legacyPostId}`);
      } catch (err) {
        //any general errors from BE come as string instead of object - hope to improve error handling on BE soon
        try {
          const { value } = JSON.parse(err);
          const {
            productErrors: responseProductErrors,
            variantErrors: responseVariantErrors,
          } = value;
          setFormErrors({ ...responseProductErrors });
          if (responseVariantErrors) {
            _.forEach(productCatalogVariants, (variant, index) => {
              const { sku } = variant;
              setVariantErrors((prevState) => {
                const newState = _.cloneDeep(prevState);
                newState[index] = responseVariantErrors[sku];
                return newState;
              });
            });
          }
          showErrorToast(
            'Form Validation Failed',
            'There are some input fields that need to be addressed before you can successfully save this product. Please update and try again.'
          );
          setLoading(false);
        } catch {
          const toastTitle = isSaved
            ? 'Error Updating Product'
            : 'Error Creating Product';
          //we want to ensure that err is a string to prevent any type errors when rendering the toast
          showErrorToast(toastTitle, _.toString(err));
          setLoading(false);
        }
      }
    } else {
      setLoading(false);
    }
  };

  const handlePublicProductFieldsError = (fieldName, errorMessage) => {
    setRequiredPublicProductFields((prevState) => {
      return {
        ...prevState,
        [fieldName]: errorMessage,
      };
    });
  };

  const handlePublicVariantFieldsError = (
    fieldName,
    variantIndex,
    errorMessage
  ) => {
    setRequiredPublicVariantFields((prevState) => {
      const newState = _.cloneDeep(prevState);
      const error = {
        [fieldName]: errorMessage,
      };
      newState[variantIndex] = {
        ...prevState[variantIndex],
        ...error,
      };
      return newState;
    });
  };

  // remove field from error object when user changes input value
  const removeRequiredProductFields = useCallback(
    (fieldName) => {
      setRequiredPublicProductFields((prevState) => {
        const newState = _.cloneDeep(prevState);
        if (newState[fieldName]) {
          delete newState[fieldName];
        }
        return newState;
      });
    },
    [setRequiredPublicProductFields]
  );

  const handleRequiredInputChange = useCallback(
    (e) => {
      removeRequiredProductFields(e.target.name);
      handleInputChange(e);
    },
    [removeRequiredProductFields, handleInputChange]
  );

  const handleClickDisabledPublicSwitch = () => {
    setRequiredPublicVariantFields([]);
    validateRequiredPublicFields(
      productFormData,
      handlePublicProductFieldsError,
      handlePublicVariantFieldsError
    );
  };

  const handleClickCancel = () => {
    history.push('/admin/product-catalog');
    deleteUnsavedImages();
  };

  const publicSwitchEnabled = useMemo(
    () => getPublicSwitchEnabled(productFormData),
    [productFormData]
  );

  const hasVariantErrors = !_.every(variantErrors, (error) => _.isEmpty(error));

  const { disableProductCatalogSave: disableProductCatalogSaveFF } = useFlags();
  const saveButtonDisabled = disableProductCatalogSaveFF ||
    !_.isEmpty(productFormErrors) || hasVariantErrors || loading || !isUpdated;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{title}</h1>
        <div className={styles.toggleSwitch}>
          <span
            data-tip="The switch will turn active when all the required fields have values. Click to highlight the missing fields."
            data-tip-disable={publicSwitchEnabled}
            data-for="switch"
          >
            <ToggleSwitch
              id="isPublic"
              name="isPublic"
              checked={isPublic || false}
              disabled={!publicSwitchEnabled}
              labelOff="Private"
              labelOn="Public"
              onChange={handleCheckbox}
              onClickDisabled={handleClickDisabledPublicSwitch}
            />
          </span>
        </div>
      </div>
      <AdminFormRow>
        <div className={styles.tabs}>
          <Tab
            onClick={() => handleClickTab('productInformation')}
            selected={tab === 'productInformation'}
            title="Product Information"
          />
          <span
            data-tip="To access the Product Metadata tab, please save a product on the current tab."
            data-tip-disable={isSaved}
            data-for="metadata"
          >
            <Tab
              onClick={() => handleClickTab('productMetadata')}
              selected={tab === 'productMetadata'}
              title="Product Metadata"
              disabled={!isSaved}
            />
          </span>
        </div>
      </AdminFormRow>
      {tab === 'productInformation' && (
        <ProductInformationForm
          formData={productFormData}
          formErrors={productFormErrors}
          requiredPublicProductFields={requiredPublicProductFields}
          requiredPublicVariantFields={requiredPublicVariantFields}
          handleInputChange={handleInputChange}
          handleRequiredInputChange={handleRequiredInputChange}
          handleCheckbox={handleCheckbox}
          setFormData={setFormData}
          handleErrors={handleErrors}
          setRequiredPublicVariantFields={setRequiredPublicVariantFields}
          resetField={resetField}
          variantErrors={variantErrors}
          setVariantErrors={setVariantErrors}
        />
      )}
      {tab === 'productMetadata' && (
        <ProductMetadataForm
          productFormData={productFormData}
          register={register}
          reset={reset}
          watch={watch}
          setValue={setValue}
          initialMetadataMap={initialMetadataMap}
          metadataFormData={metadataFormData}
        />
      )}
      <AdminFormRow>
        <Button
          onClick={onSubmit}
          style={{ width: '120px' }}
          disabled={saveButtonDisabled}
        >
          {loading ? <div className={styles.loading}>Saving</div> : 'Save'}
        </Button>
        <Button
          style={{ width: '120px' }}
          buttonType={BUTTON_TYPES.CANCEL}
          onClick={handleClickCancel}
        >
          Cancel
        </Button>
      </AdminFormRow>
      <ReactTooltip
        id="metadata"
        place="top"
        type="dark"
        effect="solid"
        className={styles.tooltip}
      />
      <ReactTooltip
        id="switch"
        place="right"
        type="dark"
        effect="solid"
        className={styles.tooltip}
      />
    </div>
  );
};

ProductForm.propTypes = {
  title: PropTypes.string.isRequired,
  initialProductData: PropTypes.shape({}),
  initialMetadataMap: PropTypes.shape({}),
  setInitialProductData: PropTypes.func,
  setInitialMetadataMap: PropTypes.func,
};

ProductForm.defaultProps = {
  initialProductData: null,
  initialMetadataMap: null,
  setInitialProductData: () => {},
  setInitialMetadataMap: () => {},
};

export default ProductForm;
